import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    Form,
    FormControl,
    FormControlStack,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { useListServicePlans } from 'src/features/servicePlans/api/useListServicePlans';
import { SubscriptionFormSchemaValues } from 'src/features/subscriptions/utils/formSchema';
import { customersMessages } from 'src/features/customers/utils/messages';
import { useListCustomers } from 'src/features/customers/api/useListCustomers';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { TRIAL_MODE } from 'src/features/subscriptions/utils/types';
import { TRIAL_MODE_MAP } from 'src/features/subscriptions/utils/constants';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS } from 'src/features/moneyAccounts/utils/constants';
import { useParams } from 'react-router-dom';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';
import React from 'react';

type SubscriptionProps = {
    form: UseFormReturn<SubscriptionFormSchemaValues>;
    onSubmit: (values: SubscriptionFormSchemaValues) => void;
    /** Trial fields are create-only, and the plan cannot be changed by a direct update. */
    isUpdate: boolean;
};

function SubscriptionForm({ onSubmit, form, isUpdate }: SubscriptionProps) {
    const { formatMessage } = useIntl();

    const { customerId } = useParams();
    const [searchCustomer, setSearchCustomer] = React.useState<string>('');
    const [searchServicePlan, setSearchServicePlan] = React.useState<string>('');

    const disableCustomerSelect = Boolean(customerId);

    const { data: servicePlans } = useListServicePlans({
        search: searchServicePlan,
    });
    const { data: customers } = useListCustomers({
        search: searchCustomer,
    });

    const { start_date } = form.getValues();

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <SearchSelectFormField
                name="service_plan"
                control={form.control}
                disabled={isUpdate}
                label={formatMessage(servicePlansMessages.servicePlan)}
                items={servicePlans?.results ?? []}
                clearable
                placeholder={`${formatMessage(servicePlansMessages.servicePlan)}...`}
                searchPlaceholder={formatMessage(servicePlansMessages.searchServicePlan)}
                emptyTitle={formatMessage(servicePlansMessages.noServicePlan)}
                emptyDescription={formatMessage(servicePlansMessages.noServicePlanFound)}
                getItemValue={(plan) => plan.id}
                getItemLabel={(plan) => plan.name}
                getItemKeywords={(plan) => [plan.name]}
                loadOptions={setSearchServicePlan}
            />
            {isUpdate && <FormDescription>{formatMessage(subscriptionsMessages.planChangeHint)}</FormDescription>}
            <SearchSelectFormField
                name="customer"
                control={form.control}
                label={formatMessage(customersMessages.customer)}
                items={customers?.results ?? []}
                clearable
                placeholder={`${formatMessage(customersMessages.customer)}...`}
                searchPlaceholder={formatMessage(customersMessages.searchCustomer)}
                emptyTitle={formatMessage(customersMessages.noCustomer)}
                emptyDescription={formatMessage(customersMessages.noCustomerFound)}
                getItemValue={(customer) => customer.id}
                getItemLabel={(customer) => customer.name}
                getItemKeywords={(customer) => [customer.name]}
                disabled={disableCustomerSelect}
                loadOptions={setSearchCustomer}
            />
            <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(subscriptionsMessages.startDate)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    captionLayout="dropdown"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(subscriptionsMessages.endDate)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    captionLayout="dropdown"
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabledDateRange={{ before: new Date(start_date) }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            {!isUpdate && (
                <>
                    <SelectFormField<SubscriptionFormSchemaValues>
                        form={form}
                        name="trial_mode"
                        label={formatMessage(subscriptionsMessages.trial)}
                        options={Object.values(TRIAL_MODE)}
                        optionTranslationMap={TRIAL_MODE_MAP}
                    />
                    {form.watch('trial_mode') === TRIAL_MODE.CUSTOM && (
                        <FormField
                            control={form.control}
                            name="trial_end"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog" required>
                                        {formatMessage(subscriptionsMessages.trialEnd)}
                                    </FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            <CalendarWithTime
                                                captionLayout="dropdown"
                                                dateISO={field.value ?? undefined}
                                                onChange={field.onChange}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                    )}
                </>
            )}
            <SelectFormField<SubscriptionFormSchemaValues>
                form={form}
                name="has_active_billing"
                isBoolean={true}
                required
                label={formatMessage(subscriptionsMessages.activeBilling)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />
        </Form>
    );
}

export default SubscriptionForm;
